import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Form, Row, Col } from 'antd';
import Icon from '@ant-design/icons';
import styles from './TransactionResultModal.module.less';
import Modal from '../Modal/Modal';
import { Button } from '../Button/Button';
import FailedIcon from '../../assets/img/failedResult.svg?react';
import SuccessIcon from '../../assets/img/successResult.svg?react';
import classnames from 'classnames';
import useAnalytika from '../../utils/analytika/reporterAnalytika';
import { AnalytikaEventsAttrType } from '../../api/dtos/analytika';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';

interface TransactionResultModalProps {
  isSuccess: boolean;
  visible: boolean;
  onClose: () => void;
  analytikaCancelEvent: AnalytikaEventsAttrType;
  analytikaProceedEvent: AnalytikaEventsAttrType;
  analytikaOpenEvent: AnalytikaEventsAttrType;
  onProceed?: () => void;
  notificationTitle?: string | React.ReactElement;
  notificationText?: string | React.ReactElement;
  primaryButtonText?: string;
  secondaryButtonText?: string;
}

const TransactionResultModal = ({
  visible,
  onClose,
  isSuccess,
  notificationTitle,
  notificationText,
  primaryButtonText,
  secondaryButtonText,
  onProceed,
  analytikaCancelEvent,
  analytikaProceedEvent,
  analytikaOpenEvent
}: TransactionResultModalProps) => {
  const { t } = useTranslation();
  const { fireEvent } = useAnalytika();

  useEffect(() => {
    visible && fireEvent(analytikaOpenEvent);
  }, [visible]);

  const handleCancel = () => {
    fireEvent(analytikaCancelEvent);
    publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_BACK_CREDIT_ADDED, event_version: 1 });
    onClose();
  };

  const redirectToProfile = () => {
    fireEvent(analytikaProceedEvent);
    publishAV2Event({ event_name: ANALYTIKA_EVENTS_V2.EXPR_BACK_CREDIT_ADDED, event_version: 1 });
    onProceed && onProceed();
  };

  return (
    <Modal
      className={styles.modal}
      visible={visible}
      onClose={handleCancel}
      footer={[
        primaryButtonText && (
          <Button
            key="paymentMethods"
            block
            type={'primary'}
            onClick={redirectToProfile}
            data-testid="buttonPaymentMethods"
          >
            {primaryButtonText}
          </Button>
        ),
        secondaryButtonText && (
          <Button key="back" block onClick={handleCancel} data-testid="buttonCancelModal">
            {secondaryButtonText}
          </Button>
        )
      ]}
    >
      <div data-testid="transactionResult" className={styles.content}>
        <Row>
          <Col xs={24} md={24} lg={24}>
            <Form layout="vertical" hideRequiredMark>
              <Row>
                <Col xs={24} md={24} lg={24}>
                  <div className={styles.failedIcon}>
                    <Icon style={{ fontSize: 150 }} component={() => (isSuccess ? <SuccessIcon /> : <FailedIcon />)} />
                  </div>
                </Col>
                {!isSuccess && (
                  <Col className={classnames([styles.notification, styles.firstLine])} xs={24} md={24} lg={24}>
                    <Typography.Text strong>{t('modal.transactionResult.commonTitle')}</Typography.Text>
                  </Col>
                )}
                <Col className={styles.notification} xs={24} md={24} lg={24}>
                  <Typography.Text strong>{notificationTitle}</Typography.Text>
                </Col>
                <Col className={styles.notification} xs={24} md={24} lg={24}>
                  <Typography.Text type={'secondary'}>{notificationText}</Typography.Text>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default TransactionResultModal;
